import { useQuery } from "@tanstack/react-query";

import { atlasRoutes } from "@/app/router/routes";
import { EventTimeline, SpecialProperty } from "@/components/timeline";
import {
  AudioRecordingEventInternalResponseSchema,
  fetchAudioRecordingEventsInternal,
} from "@/pages/audio-recordings/shared/api";

interface AudioRecordingEventTimelineProps {
  audioRecordingId: string;
  createdAt?: string;
}

// Extract special properties that should be rendered as links
const getSpecialProperties = (properties: Record<string, unknown>): SpecialProperty[] => {
  const special: SpecialProperty[] = [];

  if (properties.suggestion_id && typeof properties.suggestion_id === "string") {
    special.push({
      key: "suggestion_id",
      label: "Note Suggestion",
      id: properties.suggestion_id,
      route: atlasRoutes.noteSuggestionsView({
        params: { noteSuggestionId: properties.suggestion_id },
      }).path,
    });
  }

  if (properties.note_id && typeof properties.note_id === "string") {
    special.push({
      key: "note_id",
      label: "Note",
      id: properties.note_id,
      route: atlasRoutes.notesView({ params: { noteId: properties.note_id } }).path,
    });
  }

  return special;
};

export const AudioRecordingEventTimeline = ({
  audioRecordingId,
  createdAt,
}: AudioRecordingEventTimelineProps) => {
  const query = useQuery({
    queryKey: ["audio-recording-events", audioRecordingId],
    queryFn: () => fetchAudioRecordingEventsInternal(audioRecordingId),
    enabled: Boolean(audioRecordingId),
    staleTime: 30 * 1000,
  });

  const events = query.data?.events ?? [];
  const total = query.data?.total ?? 0;

  return (
    <EventTimeline<AudioRecordingEventInternalResponseSchema>
      events={events}
      total={total}
      isLoading={query.isLoading}
      isError={query.isError}
      createdAt={createdAt}
      createdLabel="Recording Created"
      emptyMessage="No events recorded yet for this audio recording."
      getSpecialProperties={getSpecialProperties}
    />
  );
};
